import { useQuery } from "react-query";
import queryClient, { QueryKey } from "../state/QueryStore";
import {
  getEvaluationRun,
  getEvaluationRunDatasets,
  getEvaluationRunMetrics,
  getEvaluationRuns,
  getEvaluationRunTestCases,
} from "../services/EvaluationRunService";
import { EvaluationRunFetchOptions } from "../models/types";
import { getVersionHistory } from "../services/VersionHistoryService";

export const useEvaluationRuns = (params: EvaluationRunFetchOptions) => {
  return useQuery([QueryKey.EvaluationRun, params], async () => {
    const { data } = await getEvaluationRuns(params);
    return data;
  });
};

export const useEvaluationRun = (evaluationId: string, version_num?: number) => {
  const searchForVersionNum = !!version_num;
  const queryKey = searchForVersionNum
    ? [QueryKey.EvaluationRun, evaluationId, version_num]
    : [QueryKey.EvaluationRun, evaluationId];
  const queryFn = searchForVersionNum
    ? async () => {
        const { data } = await getVersionHistory({
          object_id: [evaluationId],
          version_num: [version_num],
        });
        return JSON.parse(data.results[0].object);
      }
    : async () => {
        if (evaluationId === "") return null;
        const { data } = await getEvaluationRun(evaluationId);
        return data;
      };
  return useQuery(queryKey, queryFn);
};

export const fetchEvaluationRuns = (params: EvaluationRunFetchOptions) => {
  return queryClient.fetchQuery([QueryKey.EvaluationRun, params], async () => {
    const { data } = await getEvaluationRuns(params);
    return data;
  });
};

export const fetchEvaluationRun = (evaluationId: string) => {
  return queryClient.fetchQuery([QueryKey.EvaluationRun, evaluationId], async () => {
    const { data } = await getEvaluationRun(evaluationId);
    return data;
  });
};

export const searchEvaluationRun = async (searchText: string) => {
  const evaluations = await fetchEvaluationRuns({ search: searchText });
  return evaluations.results;
};

export const useEvaluationRunTestCases = (params: EvaluationRunFetchOptions) => {
  return useQuery([QueryKey.EvaluationRunTestCase, params.evaluation_run], async () => {
    const { data } = await getEvaluationRunTestCases(params.evaluation_run ?? "");
    return data;
  });
};

export const useEvaluationRunDatasets = (params: EvaluationRunFetchOptions) => {
  return useQuery([QueryKey.EvaluationRunDataset, params.evaluation_run], async () => {
    const { data } = await getEvaluationRunDatasets(params.evaluation_run ?? "");
    return data;
  });
};

export const useEvaluationRunMetrics = (params: EvaluationRunFetchOptions) => {
  return useQuery([QueryKey.EvaluationRunMetric, params.evaluation_run], async () => {
    const { data } = await getEvaluationRunMetrics(params.evaluation_run ?? "");
    return data;
  });
};
